import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  validateEmail,
  validatePassword,
  validatePhone,
} from "../utils/formValidation";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AUTH_FIELD_BG,
  AuthFieldLabel,
  AuthLink,
  AuthPage,
  AuthStagger,
  authSubmitSx,
} from "../components/auth/AuthLayout";
import CustomTextField from "../components/CustomTextField";
import CustomCheckbox from "../components/CustomCheckbox";
import { getAuthToken, getUserId, login } from "../services/authService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useUser } from "../contexts/UserContext";
import { registerBackofficeWebPush } from "../services/backofficeWebPush";
import SuperadminOtpStep from "../components/superadmin/SuperadminOtpStep";
import { isSuperadminAuthenticated } from "../services/superadminService";

/**
 * One field, two outcomes: an email, or a superadmin's phone number. Digits
 * (optionally with spaces, dashes, brackets or a leading +) mean a phone, and
 * the backend does not ask for a password in that case.
 */
const looksLikePhone = (value: string): boolean => {
  const cleaned = value.replace(/[\s()+-]/g, "");
  return cleaned.length > 0 && /^\d+$/.test(cleaned);
};

interface OtpChallenge {
  phone: string;
  channel: string;
  message: string;
  expiresInMinutes: number;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refreshUser } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);

  const phoneMode = looksLikePhone(email);

  useEffect(() => {
    if (isSuperadminAuthenticated()) {
      navigate("/superadmin/organizations", { replace: true });
      return;
    }
    const user = localStorage.getItem("user");
    const token = getAuthToken();
    const userId = getUserId();
    if (user && token && userId) {
      const from = (location.state as any)?.from || '/home';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const newErrors: { email?: string; password?: string } = {};

    if (phoneMode) {
      const phoneResult = validatePhone(email.replace(/[\s()+-]/g, ""));
      if (!phoneResult.valid)
        newErrors.email = "Enter a valid email address or phone number";
    } else {
      const emailResult = validateEmail(email);
      const passwordResult = validatePassword(password);
      if (!emailResult.valid && emailResult.error)
        newErrors.email = emailResult.error;
      if (!passwordResult.valid && passwordResult.error)
        newErrors.password = passwordResult.error;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }
    try {
      const result = await login(email.trim(), phoneMode ? undefined : password);
      if (result.success && result.mode === "SUPERADMIN_OTP_REQUIRED") {
        // Hold the number — verify-otp needs it and the response does not echo it.
        setOtpChallenge({
          phone: email.trim(),
          channel: result.channel,
          message: result.message,
          expiresInMinutes: result.expiresInMinutes,
        });
        setPassword("");
        setLoading(false);
        return;
      }
      if (result.success) {
        toast.success("Login successful!", { position: "top-right" });
        registerBackofficeWebPush().catch((error) => {
          console.warn("Backoffice web push registration failed", error);
        });
        await refreshUser();
        // Get the redirect path from location state, or default to home
        const from = (location.state as any)?.from || '/home';
        navigate(from, { replace: true });
      } else {
        const message =
          (result as any)?.error ||
          (result as any)?.message ||
          "Login failed. Please check your credentials.";
        toast.error(message, { position: "top-right", toastId: String(message) });
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Login failed. Please try again.";
      toast.error(errorMessage, { position: "top-right", toastId: String(errorMessage) });
    }
    setLoading(false);
  };

  const submitLabel = loading
    ? phoneMode
      ? "Sending code..."
      : "Logging in..."
    : phoneMode
    ? "Send code"
    : "Log in";

  return (
    <AuthPage
      title="Log in"
      subtitle="Welcome back — enter your details to continue."
      hideHeading={!!otpChallenge}
      stepKey={otpChallenge ? "otp" : "login"}
      footer={
        otpChallenge ? undefined : (
          <>
            Don't have an account? <AuthLink to="/signup">Sign up</AuthLink>
          </>
        )
      }
    >
      {otpChallenge ? (
        <AuthStagger>
        <SuperadminOtpStep
          phone={otpChallenge.phone}
          channel={otpChallenge.channel}
          message={otpChallenge.message}
          expiresInMinutes={otpChallenge.expiresInMinutes}
          onBack={() => setOtpChallenge(null)}
        />
        </AuthStagger>
      ) : (
        <AuthStagger component="form" startMs={120} sx={{ width: "100%" }} onSubmit={handleSubmit}>
          <AuthFieldLabel>Email</AuthFieldLabel>
          <CustomTextField
            label=""
            type="text"
            value={email}
            bgColor={AUTH_FIELD_BG}
            placeholder="you@company.com"
            autoComplete="username"
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2.5 }}
            error={!!errors.email}
            helperText={errors.email}
          />
          {!phoneMode && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <AuthFieldLabel>Password</AuthFieldLabel>
                <AuthLink
                  to="/forgot-password"
                  sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                >
                  Forgot password?
                </AuthLink>
              </Box>
              <CustomTextField
                label=""
                type={showPassword ? "text" : "password"}
                value={password}
                bgColor={AUTH_FIELD_BG}
                placeholder="Enter your password"
                autoComplete="current-password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                required
                fullWidth
                sx={{ mb: 1.5 }}
                error={!!errors.password}
                helperText={errors.password}
                endIcon={
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </>
          )}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, ml: -0.5 }}>
            <CustomCheckbox
              checked={remember}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRemember(e.target.checked)
              }
              ariaLabel="Remember me"
            />
            <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>
              Remember me
            </Typography>
          </Box>
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : undefined
            }
            sx={authSubmitSx}
          >
            {submitLabel}
          </Button>
        </AuthStagger>
      )}
    </AuthPage>
  );
};

export default Login;
